import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { getToken } from "../auth";

const API = "https://api.vyapakprint.com/api";

const OrderDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { orderId } = useParams();

  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);
  const [error, setError] = useState("");
  const [modalImage, setModalImage] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(false);

  const getErrorMessage = (err, fallback = "Something went wrong.") =>
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback;

  useEffect(() => {
    if (location.state?.order) return;

    let alive = true;

    const fetchOrder = async () => {
      try {
        setLoading(true);
        setError("");

        const token = getToken();

        const res = await axios.get(
          `${API}/businessOrders/getBusinessOrderById/${orderId}`,
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {},
        );

        if (!alive) return;

        const fetchedOrder =
          res?.data?.businessOrder ||
          res?.data?.BusinessOrder ||
          res?.data?.order ||
          res?.data;

        setOrder(fetchedOrder || null);
      } catch (err) {
        if (!alive) return;
        console.error("Failed to fetch order details:", err);
        setError(getErrorMessage(err, "Failed to load order details."));
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    };

    fetchOrder();

    return () => {
      alive = false;
    };
  }, [orderId, location.state]);

  const money = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0";
    return n.toLocaleString("en-IN");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "UTC",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });
  };

  const statusColor = (s) => {
    const v = (s || "").toLowerCase();

    if (v === "delivered" || v === "dispatched") {
      return "bg-green-100 text-green-700 border-green-200";
    }
    if (v === "shipped" || v === "printing") {
      return "bg-blue-100 text-blue-700 border-blue-200";
    }
    if (v === "processing" || v === "pending") {
      return "bg-yellow-100 text-yellow-700 border-yellow-200";
    }
    if (v === "packing") {
      return "bg-purple-100 text-purple-700 border-purple-200";
    }
    if (v === "cancelled" || v === "canceled") {
      return "bg-red-100 text-red-700 border-red-200";
    }

    return "bg-gray-100 text-gray-700 border-gray-200";
  };

  const getFileNameFromUrl = (url) => {
    if (!url) return "order-file";
    try {
      const cleanUrl = url.split("?")[0];
      const fileName = cleanUrl.split("/").pop();
      return fileName || "order-file";
    } catch {
      return "order-file";
    }
  };

  const handleDownloadFile = async (url, filename) => {
    if (!url) return;

    try {
      setDownloadingFile(true);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename || "order-file";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Failed to download file.");
    } finally {
      setDownloadingFile(false);
    }
  };

  const latestStatus = useMemo(() => {
    if (!Array.isArray(order?.statusHistory) || !order.statusHistory.length) {
      return null;
    }
    return order.statusHistory[order.statusHistory.length - 1];
  }, [order]);

  const fileName = getFileNameFromUrl(order?.file);

  const customizations = useMemo(() => {
    if (!order) return [];

    const source =
      order.customizations && typeof order.customizations === "object"
        ? order.customizations
        : {
            dieShape: order.dieShape,
            paperType: order.paperType,
            windowCutting: order.windowCutting,
            flapOpening: order.flapOpening,
            printing: order.printing,
            laminationType: order.laminationType,
            goldFoil: order.goldFoil,
            goldColor: order.goldColor,
            spotUV: order.spotUV,
            mattelColor: order.mattelColor,
            boxPacking: order.boxPacking,
            quantityType: order.quantityType,
            size: order.size,
            gsm: order.gsm,
          };

    return Object.entries(source).filter(
      ([, value]) =>
        value !== null && value !== undefined && String(value).trim() !== "",
    );
  }, [order]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto text-center py-10 text-gray-600">
          Loading order...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto text-center py-10 text-red-600">
          {error}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto text-center py-10 text-gray-600">
          Order not found.
        </div>
      </div>
    );
  }

  const product =
    order.productId && typeof order.productId === "object"
      ? order.productId
      : null;

  const user = order.userDetails || {};

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-xl bg-white border shadow-sm hover:bg-gray-50 text-gray-800"
          >
            ← Back
          </button>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl shadow p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Order #{order?._id}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Placed on{" "}
                <span className="font-medium text-gray-700">
                  {order?.orderDate ? formatDateTime(order.orderDate) : "-"}
                </span>
              </p>
            </div>

            <div className="flex items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full border text-sm font-semibold ${statusColor(
                  order?.status,
                )}`}
              >
                {order?.status || "Unknown"}
              </span>

              <div className="text-right">
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-bold text-gray-800">
                  ₹{money(order?.totalPrice)}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <Stat label="Order ID" value={order?._id} />
            <Stat label="Quantity" value={String(order?.quantity || 0)} />
            <Stat label="Status" value={order?.status || "-"} />
            <Stat
              label="Last Update"
              value={
                latestStatus?.changedAt
                  ? formatDateTime(latestStatus.changedAt)
                  : "-"
              }
            />
          </div>
        </div>

        {/* Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Customer Details
              </h2>

              <div className="space-y-3 text-sm">
                <Row label="Job Title" value={order?.firmName} />
                <Row label="Contact Person" value={user?.contactPerson} />
                <Row label="Mobile" value={user?.phone || user?.mobileNo} />
                <Row label="Email" value={user?.email} />
                <Row
                  label="Address"
                  value={
                    user?.address ||
                    user?.postalAddress ||
                    order?.address ||
                    order?.deliveryAddress
                  }
                />
                <Row label="Business Name" value={user?.businessName} />
                <Row
                  label="Website"
                  value={
                    user?.webAddress ? (
                      <a
                        href={user.webAddress}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {user.webAddress}
                      </a>
                    ) : (
                      "-"
                    )
                  }
                />
                <Row label="Other Details" value={user?.otherDetails} />
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Summary</h2>

              <div className="space-y-4 text-sm">
                <Row label="Order Date" value={formatDate(order?.orderDate)} />
                <Row
                  label="Total Price"
                  value={`₹${money(order?.totalPrice)}`}
                />
                <Row label="Quantity" value={order?.quantity || 0} />
                <Row
                  label="Latest Remark"
                  value={latestStatus?.remark || order?.remark || "-"}
                />
                <Row
                  label="Product Category"
                  value={
                    product?.category?.name ||
                    order?.category?.name ||
                    order?.category ||
                    "-"
                  }
                />
                <Row
                  label="Shipping Time"
                  value={product?.shippingTime || order?.shippingTime || "-"}
                />
              </div>
            </div>

            {customizations.length > 0 && (
              <div className="bg-white rounded-2xl shadow p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4">
                  Customizations
                </h2>

                <div className="space-y-2">
                  {customizations.map(([key, value]) => (
                    <Chip
                      key={key}
                      label={key.replace(/([A-Z])/g, " $1").trim()}
                      value={String(value)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Product Details
              </h2>

              <div className="border rounded-2xl p-4 bg-gray-50">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="w-full md:w-40 h-40 rounded-xl overflow-hidden border bg-white flex items-center justify-center">
                    {product?.image || order?.productImage ? (
                      <img
                        src={product?.image || order?.productImage}
                        alt={product?.name || order?.productName || "Product"}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs text-gray-400 px-2 text-center">
                        No image
                      </span>
                    )}
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                      <div>
                        <h3 className="text-base font-bold text-gray-800">
                          {product?.name || order?.productName || "N/A"}
                        </h3>

                        <p className="text-sm text-gray-600 mt-1">
                          Category:{" "}
                          <span className="font-medium">
                            {product?.category?.name ||
                              order?.category?.name ||
                              order?.category ||
                              "-"}
                          </span>
                        </p>

                        <p className="text-xs text-gray-500 mt-1 break-all">
                          Product ID:{" "}
                          {product?._id || order?.productId?._id || "-"}
                        </p>

                        {(product?.description || order?.description) && (
                          <p className="text-sm text-gray-600 mt-2">
                            {product?.description || order?.description}
                          </p>
                        )}
                      </div>

                      <div className="text-right">
                        <p className="text-sm text-gray-700">
                          Qty:{" "}
                          <span className="font-semibold">
                            {order?.quantity || 0}
                          </span>
                        </p>
                        <p className="text-sm text-gray-700">
                          Total:{" "}
                          <span className="font-semibold">
                            ₹{money(order?.totalPrice)}
                          </span>
                        </p>
                        <p className="text-sm font-bold text-gray-800 mt-1">
                          Dispatch Time:{" "}
                          {product?.shippingTime || order?.shippingTime || "-"}
                        </p>
                      </div>
                    </div>

                    {customizations.length > 0 && (
                      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        {customizations.map(([key, value]) => (
                          <MiniChip
                            key={key}
                            label={key.replace(/([A-Z])/g, " $1").trim()}
                            value={String(value)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Uploaded File
              </h2>

              {order?.file ? (
                <div className="border rounded-2xl p-4 bg-gray-50">
                  <p className="text-sm text-gray-500 mb-1">File Name</p>
                  <p className="font-medium text-gray-800 break-all mb-4">
                    {fileName}
                  </p>

                  <div className="flex flex-wrap gap-3">
                    <a
                      href={order.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
                    >
                      View File
                    </a>

                    <button
                      type="button"
                      onClick={() => handleDownloadFile(order.file, fileName)}
                      disabled={downloadingFile}
                      className={`px-4 py-2 rounded-xl text-white ${
                        downloadingFile
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700"
                      }`}
                    >
                      {downloadingFile ? "Downloading..." : "Download File"}
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">No file uploaded.</p>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Status History
              </h2>

              {Array.isArray(order?.statusHistory) &&
              order.statusHistory.length > 0 ? (
                <div className="space-y-4">
                  {order.statusHistory.map((item, index) => (
                    <div
                      key={index}
                      className="border rounded-2xl p-4 bg-gray-50"
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-2">
                        <span
                          className={`px-3 py-1 rounded-full border text-sm font-semibold w-fit ${statusColor(
                            item?.status,
                          )}`}
                        >
                          {item?.status || "N/A"}
                        </span>

                        <span className="text-sm text-gray-500">
                          {formatDateTime(item?.changedAt)}
                        </span>
                      </div>

                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Remark:</span>{" "}
                        {item?.remark || "N/A"}
                      </p>

                      {item?.image ? (
                        <div className="mt-3">
                          <button
                            onClick={() => setModalImage(item.image)}
                            className="text-blue-600 hover:underline text-sm mb-3"
                          >
                            View Status Image
                          </button>

                          <img
                            src={item.image}
                            alt="Status"
                            className="w-full max-w-xs h-48 object-contain rounded-lg border bg-white cursor-pointer"
                            onClick={() => setModalImage(item.image)}
                          />
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No status history available.</p>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow p-6">
              <h2 className="text-lg font-bold text-gray-800 mb-4">
                Payment / Order Info
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <Stat
                  label="Total Price"
                  value={`₹${money(order?.totalPrice)}`}
                />
                <Stat
                  label="Order Date"
                  value={formatDateTime(order?.orderDate)}
                />
                <Stat label="Status" value={order?.status || "-"} />
                <Stat label="Job Title" value={order?.firmName || "-"} />
                <Stat
                  label="Contact Person"
                  value={user?.contactPerson || "-"}
                />
                <Stat
                  label="File Uploaded"
                  value={order?.file ? "Yes" : "No"}
                />
              </div>
            </div>

            {(order?.remark || order?.notes) && (
              <div className="bg-white rounded-2xl shadow p-6">
                <h2 className="text-lg font-bold text-gray-800 mb-4">
                  Additional Notes
                </h2>
                <p className="text-gray-700">{order?.remark || order?.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {modalImage && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-4 max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Status Image</h3>
              <button
                onClick={() => setModalImage(null)}
                className="text-gray-500 hover:text-gray-800 text-2xl"
              >
                ×
              </button>
            </div>

            <img
              src={modalImage}
              alt="Status update"
              className="max-w-full max-h-[75vh] mx-auto object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

/** Helpers */
const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <p className="text-gray-500">{label}</p>
    <div className="text-gray-800 font-medium text-right break-words max-w-[60%]">
      {value || "-"}
    </div>
  </div>
);

const Chip = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-2 bg-gray-50 border rounded-lg px-3 py-2">
      <span className="text-gray-500 capitalize">{label}</span>
      <span className="font-semibold text-gray-800 text-right">{value}</span>
    </div>
  );
};

const MiniChip = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-2 bg-white border rounded-lg px-3 py-2">
      <span className="text-gray-500">{label}</span>
      <span className="font-semibold text-gray-800">{value}</span>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-gray-50 rounded-xl p-4 border">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 break-all">
      {value || "-"}
    </p>
  </div>
);

export default OrderDetails;
